import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Authorizations as GlobalAuthorizations } from "~/api/types";
import { canCollectionMember } from "~/helpers/authorizations";
import { useSetFocusKeyword } from "~/hooks/useSetFocusKeyword";

import { apiClient } from "../../api/ApiClient";
import { Icon } from "../../assets/icons/Icon";
import Button from "../mantine/button/Button";
import actionStyles from "../ui/dataTable/ActionColumn.module.scss";
import { ActionColumn } from "../ui/dataTable/DataTable";
import Modal from "../ui/modal/Modal";
import Spinner from "../ui/spinner/Spinner";
import styles from "./Actions.module.scss";
import EditKeywordModal from "./EditKeywordModal";

interface Keyword {
  id: number;
  name: string;
  refresh_frequency_override?: number | undefined;
  location_id?: number;
  location_name?: string;
  country?: string;
  language?: string;
  ranking_url?: string;
}

export interface Authorizations {
  member: string[] | undefined;
  collection: string[] | undefined;
}

interface ActionPopoverProps {
  keywordId: number;
  keyword: Keyword;
  rowIndex: number;
  authorizations: Authorizations;
  refetch: () => void;
}

const Actions: React.FC<ActionPopoverProps> = ({
  keywordId,
  keyword,
  rowIndex,
  authorizations,
  refetch,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { id } = useParams();
  const auth = authorizations as unknown as GlobalAuthorizations;
  const hasUpdatePermission = canCollectionMember(auth, rowIndex, "update");
  const hasRefreshPermission = canCollectionMember(auth, rowIndex, "refresh");
  const hasFocusPermission = canCollectionMember(auth, rowIndex, "focus");
  const hasDeletePermission = canCollectionMember(auth, rowIndex, "destroy");

  const { mutate: refreshKeyword, isPending: isPendingRefreshKeyword } =
    useMutation({
      mutationFn: async () => {
        const response = await apiClient.sites.keywordsRefreshCreate(
          Number(id),
          keywordId,
        );

        return response.data;
      },
      onSuccess: () => {
        enqueueSnackbar(`Keyword was successfully refreshed`, {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(`Error: ${error}`, {
          variant: "error",
          persist: true,
        });
      },
    });
  const { mutate: deleteKeyword, isPending: isPendingDeleteKeyword } =
    useMutation({
      mutationFn: async () => {
        const response = await apiClient.sites.keywordsDelete(
          Number(id),
          keywordId,
        );

        return response.data;
      },
      onSuccess: () => {
        enqueueSnackbar(`Keyword was successfully deleted`, {
          variant: "success",
        });
        refetch();

        setIsDeleteModalOpen(false);
      },
      onError: (error) => {
        enqueueSnackbar(`Error: ${error}`, {
          variant: "error",
          persist: true,
        });
      },
    });
  const { mutate: setFocusKeyword, isPending: isPendingSetFocusKeyword } =
    useSetFocusKeyword({ siteId: Number(id), onSuccess: refetch });

  return (
    <>
      <ActionColumn>
        {(close) => (
          <>
            <Link
              to={`/sites/${id}/keywords/${keywordId}`}
              className={actionStyles.popoverItem}
            >
              View Detailed Data
            </Link>
            <Button
              variant="menuItem"
              className={actionStyles.popoverItem}
              onClick={() => refreshKeyword()}
              disabled={!hasRefreshPermission}
            >
              <span className={actionStyles.popoverItemTitle}>
                {isPendingRefreshKeyword && <Spinner size="1rem" />}
                <span>Refresh Ranking Data</span>
              </span>
            </Button>
            <Button
              variant="menuItem"
              className={actionStyles.popoverItem}
              onClick={() => setFocusKeyword(keywordId)}
              disabled={!hasFocusPermission}
            >
              <span className={actionStyles.popoverItemTitle}>
                {isPendingSetFocusKeyword && <Spinner size="1rem" />}
                <span> Set As Focus Keyword</span>
              </span>
            </Button>
            <Button
              variant="menuItem"
              className={actionStyles.popoverItem}
              onClick={() => {
                close();
                setIsEditModalOpen(true);
              }}
              disabled={!hasUpdatePermission}
            >
              Edit Keyword
            </Button>
            <Button
              variant="menuItem"
              className={actionStyles.popoverItem}
              onClick={() => {
                close();
                setIsDeleteModalOpen(true);
              }}
              disabled={!hasDeletePermission}
            >
              <span className={actionStyles.popoverItemTitle}>
                {isPendingDeleteKeyword && <Spinner size="1rem" />}
                <span className={actionStyles.popoverItemRed}>
                  Delete Keyword
                </span>
              </span>
            </Button>
          </>
        )}
      </ActionColumn>
      <EditKeywordModal
        isOpen={isEditModalOpen}
        closeModal={() => setIsEditModalOpen(false)}
        onSuccess={() => {
          refetch();
        }}
        keywordId={keywordId}
        keywordName={keyword.name || ""}
        siteId={Number(id)}
        initialData={keyword}
      />
      <Modal
        isOpen={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        modalLabel={
          <h3 className={styles.modalTitle}>
            <div className={styles.dangerIconWrapper}>
              <Icon iconName="DangerIcon" width="1rem" height="1rem" />
            </div>
            Delete Keyword
          </h3>
        }
        buttonLabel="Delete"
        formId="deleteKeywordForm"
        actionButtonColor="danger"
        isLoading={isPendingDeleteKeyword}
        action={deleteKeyword}
      >
        <div className={styles.deleteConfirmation}>
          <p>
            Are you sure you want to delete the keyword "
            <strong>{keyword.name}</strong>"? All information will be lost. Are
            you sure you want to delete this keyword?
          </p>
        </div>
      </Modal>
    </>
  );
};

export default Actions;
